import { useEffect, useState } from "react";
import { Link } from "react-router";
import {
  ExternalCallUrl,
  addLocalDays,
  externalCallHost,
  isoWeekdayOf,
  type CreateOpeningsResponse,
  type GroupScheduleView,
  type Lesson,
  type PlanSummary,
} from "@edu/shared";
import * as api from "../../lib/api.ts";
import { SegmentedRadio } from "../../lib/SegmentedRadio.tsx";
import { Dialog } from "../shell/Dialog.tsx";
import { dayTitle, errorText } from "./common.ts";
import { NO_GROUP_SCHEDULE, OPEN_GROUP_SCHEDULE } from "./group-text.ts";
import { CallLinkField, RepeatChoice, WhenFields } from "./OccurrenceFormFields.tsx";
import {
  CREATE_KIND_SEGMENTS,
  OpeningRepeatField,
  OpeningsOutcome,
  type CreateKind,
} from "./OpeningFields.tsx";
import { InviteHandoff, ScheduleSkipped } from "./OccurrenceOutcome.tsx";
import { OccurrenceRecipient, useRecipients } from "./OccurrenceRecipient.tsx";

/**
 * Создание занятия по нажатию на пустое время календаря.
 *
 * Одна форма на два исхода, и это не экономия экранов, а то, как человек про
 * это думает: он ставит занятие ученику, а «каждую неделю» это свойство
 * занятия, а не другой вид объекта. Разовое уходит в `POST /v1/schedule/lessons`
 * и создаёт урок сразу, повторяющееся в `POST /v1/schedule/series` с одним
 * слотом, у которого день недели выведен из даты нажатия.
 *
 * ⚠️ **Открывают это окно трое, и второй с третьим появились 17.09.2026**: сетка
 * расписания, кнопка «Новое занятие» над списком уроков и кнопка «Создать урок»
 * в редакторе заготовки. Других форм заведения урока в продукте не осталось, и
 * заводить их не надо: урок, заведённый человеком, всегда встаёт на дату и
 * время (решение владельца 17.09.2026). Отсюда три необязательных входа ниже,
 * заранее выбранная заготовка, заранее набранное название и урок в `onCreated`:
 * все три нужны ровно редактору, и первые два он заполняет тем, что у него уже
 * открыто. Поля от этого не запираются: передумать в окне законно. Вторым
 * значением `onCreated` уезжает дата заведённого, и читает её другой
 * вызывающий, список уроков: он после серии уводит человека в расписание и
 * обязан открыть его на нужной неделе.
 *
 * ⚠️ **Ученик заводится ОТДЕЛЬНЫМ нажатием, а не заодно с занятием.** Создание
 * ученика возвращает постоянную ссылку, которая показывается ровно один раз (в
 * базе только SHA-256), и утопить её внутри диалога, который человек вот-вот
 * закроет, значит потерять ссылку и заставить её перевыпускать.
 *
 * ⚠️ **Отдельного нажатия для этого мало, и до 03.09.2026 ссылка терялась.**
 * Довод выше отодвигал потерю на шаг, а не закрывал её: ссылка стояла в окне
 * голым текстом, а окно после успешного заведения занятия закрывало себя само.
 * Закрыто это тремя вещами, и ни одна из них ничего не запрещает: копирование
 * одним нажатием (`CopyLink`, и выделение из поля считается наравне с ним),
 * список выданных ссылок вместо одного поля (второе заведение затирало первую
 * ссылку молча) и остановка автозакрытия, пока хоть одну ссылку не забрали
 * (`InviteHandoff`).
 *
 * ⚠️ **Вопроса «точно закрыть?» здесь нет, и заводить его не надо.** Он тут
 * стоял ровно один день. Ответ на такой вопрос это повторение того же жеста, то
 * есть нажатие, после которого видимо не происходит ничего; читалке его не
 * слышно вовсе; а охраняет он дешёвое и поправимое (ссылку, которую выпустили
 * секунду назад и никому не отдали). Двухшаговое подтверждение в кабинете
 * потрачено на одно действие, на отзыв ссылки, и оно необратимо.
 *
 * ⚠️ **Момент считает сервер.** Отсюда уезжают дата, время и зона текстом:
 * ICU в браузере это другая база часовых поясов, и слепок, посчитанный здесь,
 * разошёлся бы с занятиями серий на зоне, чьи правила поменялись между
 * релизами.
 *
 * ⚠️ **Ссылка на созвон задаётся ЗДЕСЬ, а не после создания.** Режим
 * сосуществования это штатная конфигурация продукта (раздел 2 CLAUDE.md), то
 * есть занятие в Zoom это обычный случай, а не исключение. До 21.08.2026 поля
 * тут не было вовсе, и преподаватель, ведущий в Zoom, заводил занятие, а потом
 * шёл править его второй ручкой; всё это время занятие стояло в расписании без
 * адреса, по которому на него приходят.
 *
 * Поле ОДНО на оба исхода, но кладётся в разные места, и это не мелочь.
 * У разового занятия серии нет вовсе, и ссылка ложится на сам урок. У
 * повторяющегося она ложится умолчанием СЕРИИ: персональная комната Zoom (PMI)
 * у преподавателя постоянная, а положить её на каждое занятие значило бы
 * заводить копию, которую потом придётся править по одной.
 *
 * ⚠️ **Поле «Кому», а не «Ученик»: занятие заводится и группе тоже** (решение
 * владельца 30.08.2026). Отдельного раздела под групповые занятия в кабинете
 * нет: занятия заводятся в расписании, и там же выбирается, кому. Список
 * адресатов живёт в `OccurrenceRecipient.tsx`, здесь остаются три следствия,
 * видные прямо в форме:
 *
 * 1. **у группы повторение заперто на «каждую неделю»**, потому что разового
 *    группового занятия сервер не умеет вовсе (раздел 9 проекта);
 * 2. **группа с уже заведённым расписанием ведёт на экран пучка, а не на
 *    кнопку.** `POST .../slots` принимает только день и время, поэтому
 *    набранные тут название, заготовка и ссылка на созвон уехали бы в никуда
 *    без единого слова, а слот пишется в зоне ПУЧКА, и у преподавателя с
 *    разошедшимися зонами день встал бы не туда, куда он ткнул;
 * 3. **если расписание досталось не всем, диалог говорит это ДО закрытия.**
 *
 * ⚠️ **Поля при этом УБИРАЮТСЯ с экрана, а не гаснут и не остаются заполнимыми**
 * (правка по итогам ревью 30.08.2026). Дверь вместо кнопки стояла, а форма над
 * ней жила, то есть человек заполнял название, заготовку и ссылку, и переход
 * уносил всё набранное молча. Потерей набранного этот путь и обоснован, поэтому
 * она была не исправлена, а отодвинута на шаг. Так же поступает соседняя ветка,
 * рассказ о невзятых: при готовом ответе форма уступает место, а не стоит под
 * ним. Поле «Кому» остаётся: выбор адресата это выход из этой ветки, а закрытие
 * окна выходом не считается.
 *
 * ⚠️ **С 27.09.2026 у диалога второй вид, «Открыть для записи»**
 * (`docs/booking-design.md`, 3.2), и только у открытого из сетки расписания
 * (`canOpen`). Вид убирает «Кому», заготовку, название и повторение серии:
 * окно ничьё, материала у него нет, а «каждую неделю» у него это пачка
 * отдельных окон, а не серия. Дата, время, длительность и ссылка на созвон
 * общие и при смене вида не теряются.
 */
export function CreateOccurrenceDialog({
  date,
  time,
  zone,
  planId: chosenPlanId,
  title: chosenTitle,
  onClose,
  onCreated,
  canOpen = false,
}: {
  date: string;
  time: string;
  /** Зона СМОТРЯЩЕГО: он показал мышью на время именно в ней. */
  zone: string;
  /**
   * Заготовка, выбранная до открытия окна: её подставляет кнопка «Создать урок»
   * в редакторе заготовки, где человек в этой заготовке и стоит.
   *
   * ⚠️ Поле от этого не запирается. Передумать здесь законно: заготовка это
   * материал занятия, а не его свойство, и заводящий занятие из редактора
   * вправе поставить другую или не ставить никакой.
   */
  planId?: string;
  /**
   * Название занятия, подставляемое в поле: оттуда же, из редактора заготовки,
   * и там это её имя.
   *
   * ⚠️ Не украшение. До 17.09.2026 своя форма редактора подставляла имя
   * заготовки сама, и уроки в списке назывались по ней; переезд на это окно
   * оставил поле пустым, а пустое название сервер заменяет на «Занятие 17
   * сентября». Смена читается в списке уроков сразу, и просили её не мы.
   */
  title?: string;
  onClose: () => void;
  /**
   * Занятие заведено: сам урок и местная дата, на которую всё встало.
   *
   * ⚠️ Урок приезжает сюда ТОЛЬКО у разового занятия, и потому аргумент
   * необязательный: повторяющееся занятие производит серию, а уроков у неё
   * заранее нет ни одного. Читает его один вызывающий, редактор заготовки: ему
   * нужна ссылка на заведённое, чтобы не уводить человека с экрана.
   *
   * ⚠️ Дата приезжает ВСЕГДА и в зоне СМОТРЯЩЕГО: это то, что он сам набрал в
   * поле. Нужна она тому, кто после заведения уводит человека в расписание
   * (список уроков, у серии): расписание открывается на текущей неделе, а серию
   * заводят на какую угодно, и без даты человек снова не увидел бы результата.
   * Остальные вызывающие аргументов не читают вовсе.
   */
  onCreated: (lesson?: Lesson, day?: string) => Promise<void>;
  /**
   * Показать переключатель «Занятие / Открыть для записи». Только сетка
   * расписания: из списка уроков и из редактора заготовки заводят урок, и
   * открывать время там не к месту.
   */
  canOpen?: boolean;
}) {
  const [plans, setPlans] = useState<PlanSummary[] | null>(null);

  const [planId, setPlanId] = useState(chosenPlanId ?? "");
  const [repeat, setRepeat] = useState<"single" | "weekly">("single");
  const [at, setAt] = useState({ date, time });
  const [durationMin, setDurationMin] = useState(60);
  const [title, setTitle] = useState(chosenTitle ?? "");
  const [callUrl, setCallUrl] = useState("");
  const [kind, setKind] = useState<CreateKind>("lesson");
  const opening = canOpen && kind === "opening";
  /** «Каждую неделю до» у окна: пачка, а не серия (`OpeningRepeatField`). */
  const [batch, setBatch] = useState(false);
  const [until, setUntil] = useState("");
  /** Исход открытия времени, если что-то не открылось. */
  const [opened, setOpened] = useState<CreateOpeningsResponse | null>(null);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  /** Заведённое расписание группы, у которого кто-то остался без занятий. */
  const [done, setDone] = useState<GroupScheduleView | null>(null);
  /** Занятие заведено, а окно оставлено открытым ради ссылки нового ученика. */
  const [created, setCreated] = useState(false);

  const to = useRecipients((e) => setErr(errorText(e)));
  const chosen = to.chosen;
  const group = chosen !== null && chosen.kind === "group" ? chosen.choice : null;
  /** Группа с расписанием: диалог не заводит второго, а ведёт туда, где день ставится. */
  const openGroup = group !== null && group.path ? group.group.id : null;
  /** У группы занятия только еженедельные, поэтому выбор повторения ей заперт. */
  const repeatMode = group !== null ? "weekly" : repeat;

  useEffect(() => {
    void (async () => {
      try {
        const p = await api.listPlans();
        setPlans(p.plans);
      } catch (e) {
        setErr(errorText(e));
      }
    })();
  }, []);

  /**
   * Занятие заведено.
   *
   * Окно закрывается только тогда, когда терять нечего. Иначе оно остаётся со
   * ссылками на экране: форма уступает им место, потому что второе нажатие
   * «Завести» завело бы второе занятие.
   *
   * ⚠️ `unclaimed` это функция, а не признак, снятый до отправки: пока запрос
   * летит, главная кнопка погашена, а кнопка копирования нет, и человек,
   * забравший ссылку за эту секунду, получил бы окно, оставшееся открытым зря.
   */
  const finish = (skipped: GroupScheduleView | null) => {
    if (skipped !== null) setDone(skipped);
    if (to.unclaimed()) setCreated(true);
    else if (skipped === null) onClose();
  };

  /**
   * Домен ссылки, если она вообще разбирается.
   *
   * Считается той же парой функций, что и везде: схема `ExternalCallUrl`
   * решает, законен ли адрес, а `externalCallHost` достаёт домен. Своей
   * проверки здесь нет намеренно, иначе форма начала бы принимать то, что
   * отвергает сервер, или наоборот.
   */
  const callTrimmed = callUrl.trim();
  const callHost =
    callTrimmed === "" || !ExternalCallUrl.safeParse(callTrimmed).success
      ? null
      : externalCallHost(callTrimmed);

  /**
   * Открыть время для записи. Исход с пропусками остаётся в диалоге, как у
   * расписания группы; всё открылось, значит окно закрывается, и новое время
   * видно в сетке.
   */
  const submitOpening = () =>
    void (async () => {
      setBusy(true);
      setErr(null);
      try {
        const res = await api.createOpenings({
          date: at.date,
          time: at.time,
          timeZone: zone,
          durationMin,
          externalCallUrl: callTrimmed === "" ? null : callTrimmed,
          repeatUntil: batch ? until : null,
        });
        await onCreated(undefined, at.date);
        if (res.skipped.length > 0) setOpened(res);
        else onClose();
      } catch (e) {
        setErr(errorText(e));
      } finally {
        setBusy(false);
      }
    })();

  const submit = () =>
    void (async () => {
      if (chosen === null || openGroup !== null) return;
      setBusy(true);
      setErr(null);
      // Пустое поле это «созвона нет», а не пустая строка: схема на сервере
      // пустую строку отвергнет, и человек получил бы `invalid_body` вместо
      // занятия
      const call = callUrl.trim() === "" ? null : callUrl.trim();
      const lessonTitle = title.trim() === "" ? null : title.trim();
      const defaultPlanId = planId === "" ? null : planId;
      try {
        if (chosen.kind === "group") {
          /**
           * ⚠️ `studentUserId` в теле НЕТ: состав берётся из группы, иначе на
           * вопрос «кто в группе» нашлось бы два ответа. Приватной заметки у
           * пучка нет вовсе: заметку пишут ПРО ученика, а пучок это не ученик.
           */
          const view = await api.createGroupSchedule(chosen.choice.group.id, {
            timeZone: zone,
            startsOn: at.date,
            durationMin,
            lessonTitle,
            defaultPlanId,
            externalCallUrl: call,
            slots: [{ weekday: isoWeekdayOf(at.date), startsAt: at.time }],
          });
          await onCreated(undefined, at.date);
          // Кого расписание не застало, тот назван до закрытия окна: молча из
          // группового расписания не выпадает никто
          finish(view.skipped.length > 0 ? view : null);
          return;
        }
        if (repeat === "single") {
          const lesson = await api.createSingleLesson({
            studentUserId: chosen.userId,
            date: at.date,
            time: at.time,
            timeZone: zone,
            durationMin,
            title: lessonTitle,
            planId: defaultPlanId,
            // У разового занятия серии нет: ссылка ложится на сам урок
            externalCallUrl: call,
          });
          // Урок отдаём наружу ровно здесь: у серии его нет вовсе
          await onCreated(lesson, at.date);
        } else {
          /**
           * ⚠️ Зона серии это зона, в которой человек задал время, то есть его
           * собственная. Повторение привязано к ЛОКАЛЬНОМУ времени, а не к
           * моменту: иначе дважды в год все занятия уехали бы на час.
           *
           * `startsOn` это дата нажатия, а не сегодня: серия, поставленная на
           * следующий вторник, не должна производить занятие задним числом.
           */
          await api.createSeries({
            studentUserId: chosen.userId,
            timeZone: zone,
            startsOn: at.date,
            durationMin,
            lessonTitle,
            defaultPlanId,
            // ⚠️ Умолчанием СЕРИИ, а не копией на каждое занятие: комната Zoom у
            // преподавателя постоянная, а копии пришлось бы править по одной
            externalCallUrl: call,
            slots: [{ weekday: isoWeekdayOf(at.date), startsAt: at.time }],
          });
          await onCreated(undefined, at.date);
        }
        finish(null);
      } catch (e) {
        setErr(errorText(e));
      } finally {
        setBusy(false);
      }
    })();

  /*
    ⚠️ Общий `Dialog`, а не свой `div.modal`, и это была не косметика.
    Собственная разметка не давала ничего из того, что нативный `<dialog>`
    отдаёт бесплатно: закрытия по Esc, ловушки фокуса, возврата фокуса туда,
    откуда открыли, верхнего слоя и `inert` для фона. Отсутствие первого
    пункта и было жалобой 21.08.2026; остальные четыре чинятся тем же ходом.

    Это был ПОСЛЕДНИЙ самодельный модал кабинета (у проводника материалов своё
    окно, и оно оставлено как есть намеренно, см. шапку `shell/Dialog.tsx`).
  */
  /**
   * Форма уступила место ответу: кнопок в подвале больше нет.
   *
   * ⚠️ Подвал ЦЕЛИКОМ пропадает, а не гаснет: «Не надо» после заведения врало бы
   * («надо» уже случилось), а «Завести занятие» завело бы второе. Выход из этого
   * состояния один и стоит в шапке окна, это «Закрыть».
   */
  const answered = created || done !== null || opened !== null;
  /** Кривой адрес гасит кнопку у обоих видов, а не уходит в отказ сервера. */
  const badCall = callTrimmed !== "" && callHost === null;

  return (
    <Dialog
      title={opening ? "Открыть время для записи" : "Новое занятие"}
      note={`${dayTitle(at.date)}, ${at.time} · время в зоне ${zone}`}
      /*
        ⚠️ **Подвал вынесен из тела 14.09.2026** (долг подхода 2ж). Форма занятия
        это самое длинное окно кабинета: адресат, заготовка, повтор, название,
        ссылка на созвон и абзац про то, что произойдёт. Ряд кнопок стоял под
        всем этим, ВНУТРИ прокручиваемого тела, то есть главная кнопка пряталась
        под нижнюю кромку ровно тогда, когда форма заполнена и её ищут.
      */
      foot={
        answered ? undefined : (
          <>
            <button type="button" className="btn btn--quiet" disabled={busy} onClick={onClose}>
              Не надо
            </button>
            {/*
              Ради этой кнопки диалог и открыт: единственный акцент. «Завести»
              у нового ученика выше акцента не получает намеренно, хотя тоже
              создаёт: это подготовка к главному действию, а не оно само. У
              группы с расписанием на месте кнопки стоит дверь: заводить тут
              нечего, а идти есть куда
            */}
            {opening ? (
              <button
                type="button"
                className="btn btn--primary"
                // Ученика тут нет: окно ничьё, записывается любой из ваших
                disabled={busy || at.date === "" || at.time === "" || badCall || (batch && (until === "" || until < at.date))}
                onClick={submitOpening}
              >
                {busy ? "…" : "Открыть для записи"}
              </button>
            ) : openGroup !== null ? (
              <Link className="btn btn--primary" to={`/schedule/groups/${openGroup}`}>
                {OPEN_GROUP_SCHEDULE}
              </Link>
            ) : (
              <button
                type="button"
                className="btn btn--primary"
                disabled={
                  // Заведение ученика гасит главную кнопку так же, как гасило
                  // раньше: до вынесения адресата это был тот же самый `busy`
                  busy ||
                  to.adding ||
                  chosen === null ||
                  at.date === "" ||
                  at.time === "" ||
                  // Кривой адрес гасит кнопку, а не отправляется в отказ: сервер
                  // ответил бы `invalid_body`, то есть «форма собрана неверно», и
                  // человеку пришлось бы догадываться, какое именно поле не то
                  badCall
                }
                onClick={submit}
              >
                {/*
                  ⚠️ У группы нажатие заводит РАСПИСАНИЕ, и подпись обязана
                  назвать то, что заводится. «Завести серию» тут стояло до
                  ревью 30.08.2026, а соседние строки того же экрана называли
                  получившееся расписанием: одно и то же под двумя именами
                  читается как две разные вещи. Слова берутся оттуда же, откуда
                  их читает экран группы, из `group-text.ts`
                */}
                {busy
                  ? "…"
                  : group !== null
                    ? NO_GROUP_SCHEDULE.action
                    : repeatMode === "single"
                      ? "Завести занятие"
                      : "Завести серию"}
              </button>
            )}
          </>
        )
      }
      onClose={onClose}
    >
      <div className="card occ">
        {err !== null && <p className="err">{err}</p>}

        {/* Форма уступает место ответу: та же кнопка завела бы второе занятие */}
        {answered ? (
          <>
            {created && <InviteHandoff invites={to.invites} onCopied={to.markInviteCopied} />}
            {done !== null && <ScheduleSkipped view={done} nameOf={to.nameOf} />}
            {opened !== null && <OpeningsOutcome res={opened} />}
          </>
        ) : (
          <div className="form">
            {canOpen && (
              <SegmentedRadio
                ariaLabel="Что завести"
                segments={CREATE_KIND_SEGMENTS}
                value={kind}
                onPick={(id) => setKind(id === "opening" ? "opening" : "lesson")}
              />
            )}

            {opening ? (
              <>
                <WhenFields
                  date={at.date}
                  time={at.time}
                  durationMin={durationMin}
                  onDate={(v) => setAt((p) => ({ ...p, date: v }))}
                  onTime={(v) => setAt((p) => ({ ...p, time: v }))}
                  onDuration={setDurationMin}
                />
                <OpeningRepeatField
                  on={batch}
                  until={until}
                  min={at.date}
                  weekday={isoWeekdayOf(at.date)}
                  onToggle={(on) => {
                    setBatch(on);
                    // Четыре недели для начала: пустое «до» гасило бы кнопку
                    // без объяснения, а дата под рукой правится одним нажатием
                    if (on && until === "" && at.date !== "") setUntil(addLocalDays(at.date, 21));
                  }}
                  onUntil={setUntil}
                />
                <CallLinkField value={callUrl} host={callHost} onChange={setCallUrl} />
                <p className="muted">
                  Ваши ученики увидят это время в своём расписании и смогут записаться сами. Занятие
                  появится у вас сразу, подтверждать не нужно.
                </p>
              </>
            ) : (
              <OccurrenceRecipient to={to} busy={busy} />
            )}

            {opening ? null : openGroup === null ? (
              <>
                <label className="field">
                  <span className="lbl">Заготовка</span>
                  <select
                    className="fld"
                    value={planId}
                    onChange={(e) => setPlanId(e.target.value)}
                    disabled={plans === null}
                  >
                    <option value="">без заготовки</option>
                    {(plans ?? []).map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.title}
                      </option>
                    ))}
                  </select>
                </label>

                <RepeatChoice
                  weekly={repeatMode === "weekly"}
                  locked={group !== null}
                  weekday={isoWeekdayOf(at.date)}
                  onPick={setRepeat}
                />

                <WhenFields
                  date={at.date}
                  time={at.time}
                  durationMin={durationMin}
                  onDate={(v) => setAt((p) => ({ ...p, date: v }))}
                  onTime={(v) => setAt((p) => ({ ...p, time: v }))}
                  onDuration={setDurationMin}
                />

                <label className="field">
                  <span className="lbl">Название</span>
                  <input
                    className="fld"
                    value={title}
                    placeholder="можно оставить пустым"
                    onChange={(e) => setTitle(e.target.value)}
                  />
                </label>
                {/*
                  ⚠️ **Название уезжает УЧЕНИКУ**, и до этой подписи об этом не
                  говорилось нигде. Заводящий занятие ГРУППЫ почти наверняка
                  напишет сюда её название, а оно станет заголовком занятия в
                  расписании каждого ученика, где признака группы не должно быть
                  ни одного (раздел 3 `docs/group-lessons-design.md`).

                  ⚠️ Подпись стоит и у пары, хотя цена там ниже. Показанная
                  только группе, она обещала бы про пару обратное: что название
                  остаётся у преподавателя. Это неправда, оно едет обеим сторонам
                  той же ручкой сетки, а пишут в это поле «отработка» и «долг за
                  июль».

                  Слова ЗДЕСЬ, а не в `group-text.ts`: половин у развилки две,
                  вторая не про групповой экран вовсе, и разведённые по двум
                  файлам они разъедутся. В словарь строку зовут числа и
                  совпадение с отказом сервера, тут нет ни того, ни другого.
                */}
                <p className="hint">
                  {group !== null
                    ? "Каждый ученик группы увидит это название в своём расписании."
                    : "Ученик увидит это название в своём расписании."}
                </p>

                <CallLinkField value={callUrl} host={callHost} onChange={setCallUrl} />
              </>
            ) : (
              /*
                Что именно не переедет отсюда на экран группы. Про само
                расписание сказано строкой выше, в самом выбранном варианте
                («расписание уже заведено, новый день добавляется в нём»), и
                вторая копия этой мысли только сбивала бы; а вот про день с
                временем там не сказано ничего, и человек показал мышью именно
                на них
              */
              <p className="hint">День и время придётся выбрать заново.</p>
            )}

            {/*
              ⚠️ У группы этого абзаца нет вовсе (правка по итогам ревью
              30.08.2026), и причин две. Кнопки на занятии у группы не бывает:
              своей комнаты у него пока нет, и карточка её не рисует, то есть
              абзац обещал бы кнопку, которую человек станет искать и не найдёт.
              А про еженедельность у группы уже сказано строкой выше
              (`GROUP_WEEKLY_ONLY`), и третья копия одной мысли сбивает
            */}
            {group === null && !opening && (
              <p className="muted">
                {repeatMode === "single"
                  ? "Урок создастся сразу, материал он возьмёт при первом открытии доски."
                  : "Серия будет производить занятия каждую неделю. Уроки заранее не создаются: их заводит кнопка на занятии."}
              </p>
            )}
          </div>
        )}
      </div>
    </Dialog>
  );
}
